import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTeam } from '../contexts/TeamContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, Plus } from 'lucide-react';

const TeamSelection = () => {
  const { teams, switchTeam, loading } = useTeam();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (teams.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="glass-effect p-12 max-w-lg text-center">
          <Users className="w-20 h-20 mx-auto mb-6 text-gray-400" />
          <h1 className="text-3xl font-bold mb-4">No Teams Yet</h1>
          <p className="text-gray-600 mb-8">
            Create your first care team to start coordinating care for your loved one.
          </p>
          <Button
            onClick={() => navigate('/teams/create')}
            className="bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600"
            data-testid="create-team-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Your First Team
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto fade-in">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Your Care Teams</h1>
            <p className="text-gray-600">Select a team to manage</p>
          </div>
          <Button
            onClick={() => navigate('/teams/create')}
            data-testid="create-new-team-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Team
          </Button>
        </div>

        <div className="grid gap-6">
          {teams.map(team => (
            <Card
              key={team.id}
              className="glass-effect p-6 hover:shadow-xl transition-shadow cursor-pointer"
              onClick={() => {
                switchTeam(team.id);
                navigate('/dashboard');
              }}
              data-testid={`team-card-${team.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold mb-2">{team.name}</h2>
                  {team.description && (
                    <p className="text-gray-600 mb-3">{team.description}</p>
                  )}
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className={`status-badge status-${team.status}`}>
                      {team.status}
                    </span>
                    <span>
                      Created {new Date(team.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    switchTeam(team.id);
                    navigate('/dashboard');
                  }}
                >
                  View Dashboard
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default TeamSelection;